import GridPagination from "@/components/GridPagination";
import FilterOperatorInput from "@/components/FilterOperatorInput";
import uxTheme from "./uxTheme";
import type { ThemeOptions } from "@mui/material";
import { createTheme } from "@mui/material";
import { cssVar } from "@/utils";
import { blue } from "@mui/material/colors";
import type {} from "@mui/lab/themeAugmentation";
import type {} from "@mui/x-data-grid-premium/themeAugmentation";
import type {} from "@mui/x-date-pickers/themeAugmentation";
import { toggleButtonGroupClasses } from "@mui/material/ToggleButtonGroup";
import { Icon } from "@trimble/modus-cmx";

declare module "@mui/material/styles" {
  interface Palette {
    trimbleGray: Palette["primary"];
  }

  interface PaletteOptions {
    trimbleGray?: PaletteOptions["primary"];
  }
}

declare module "@mui/material/Button" {
  interface ButtonPropsColorOverrides {
    trimbleGray: true;
  }
}

// Playwright sets this flag via addInitScript before any app code runs, so that
// integration tests can query columns/rows that would otherwise be virtualized
// out of the DOM. Production users get the full virtualization perf.
const playwrightDisablesVirtualization =
  typeof window !== "undefined" &&
  window.__PW_DISABLE_DATAGRID_VIRTUALIZATION__ === true;

export const AXPAppThemeOptions: ThemeOptions = {
  ...uxTheme,
  // cssVariables: true,
  palette: {
    ...uxTheme.palette,
    trimbleGray: {
      main: cssVar("--modus-wc-color-trimble-gray"),
      contrastText: cssVar("--trimble-gray-contract-text"),
    },
  },
  components: {
    MuiCssBaseline: {
      // Modus' `input:disabled { opacity:.5 }` reveals MUI's visually-hidden
      // native inputs; re-hide them so disabled controls don't render double.
      // Target public component classes; keep PrivateSwitchBase as a fallback.
      styleOverrides: {
        ".MuiSelect-nativeInput:disabled, .MuiCheckbox-root input:disabled, .MuiRadio-root input:disabled, .MuiSwitch-root input:disabled, .PrivateSwitchBase-input:disabled":
          {
            opacity: 0,
          },
      },
    },
    MuiAlert: {
      styleOverrides: {
        standard: {
          border: "1px solid transparent",
        },
        standardInfo: {
          backgroundColor: cssVar("--info-background"),
          "& .MuiAlert-icon": {
            color: blue[700],
          },
        },
        outlinedInfo: {
          borderColor: blue[700],
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: ({ theme }) => ({
          // Find out why borderRadius can be a string
          borderRadius: Number(theme.shape.borderRadius) * 2,
        }),
      },
    },
    MuiTablePagination: {
      defaultProps: {
        slotProps: {
          select: {
            variant: "outlined",
            sx: {
              "& .MuiOutlinedInput-input": {
                paddingTop: "6px",
                paddingBottom: "6px",
              },
            },
          },
        },
      },
      styleOverrides: {
        select: {
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "var(--modus-wc-color-gray-6)",
            borderRadius: "var(--modus-wc-border-radius-input)",
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "var(--modus-wc-color-gray-8)",
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "var(--modus-wc-color-primary)",
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: ({ theme }) => ({
          // Find out why borderRadius can be a string
          borderRadius: Number(theme.shape.borderRadius) * 2,
          padding: theme.spacing(2),
        }),
      },
    },
    MuiPaper: {
      defaultProps: {
        elevation: 0,
      },
    },
    MuiDataGrid: {
      defaultProps: {
        slots: {
          basePagination: GridPagination,
        },
        slotProps: {
          loadingOverlay: {
            variant: "skeleton",
          },
          filterPanel: {
            filterFormProps: {
              // Lock the operator select on single-operator columns globally.
              operatorInputProps: {
                as: FilterOperatorInput,
              },
            },
          },
        },
        rowHeight: 76,
        disableAggregation: true,
        disableDensitySelector: true,
        disableColumnFilter: true,
        disableRowSelectionOnClick: true,
        disablePivoting: true,
        disableVirtualization: playwrightDisablesVirtualization,
      },
      styleOverrides: {
        root: ({ theme }) => ({
          color: "var(--modus-wc-color-gray-10)",
          "--unstable_DataGrid-radius": `var(--modus-wc-border-radius-table)`,
          "--DataGrid-rowBorderColor": "var(--modus-wc-color-gray-6)",
          "--unstable_DataGrid-headWeight": "var(--modus-wc-font-weight-bold)",
          backgroundColor: "transparent",
          ".MuiDataGrid-mainContent": {
            gap: theme.spacing(2),
          },
          border: 0,
          borderRadius: 0,
          "& .shaded": {
            backgroundColor: "var(--modus-wc-color-gray-1)",
            borderLeft: "1px solid var(--DataGrid-rowBorderColor)",
          },
          "& .MuiTablePagination": {
            "&-displayedRows": {
              margin: 0,
              flexGrow: 1,
            },
            "&-toolbar": {
              justifyContent: "space-between",
              padding: 0,
              minHeight: "32px",
              [theme.breakpoints.down("md")]: {
                flexDirection: "column-reverse",
                gap: 8,
                "& > *": {
                  margin: 0,
                },
              },
            },
            "&-spacer": { display: "none" },
          },
        }),
        main: {
          border: "1px solid var(--DataGrid-rowBorderColor)",
          borderRadius: "var(--unstable_DataGrid-radius)",
          backgroundColor: "var(--modus-wc-color-white)",
        },
        footerContainer: { border: 0, minHeight: "32px" },
        cell: () => ({
          // Don't display:flex the cell here, it will stop truncation from working
          "&--editing &:focus": {
            boxShadow: "unset !important",
          },
          "&:focus-within": {
            outline: "none",
          },
          "&--withLeftBorder.MuiDataGrid-cell--pinnedRight": {
            right: "0px !important",
            minWidth:
              "calc(var(--DataGrid-rightPinnedWidth) + var(--DataGrid-hasScrollY) * var(--DataGrid-scrollbarSize)) !important",
          },
        }),
        cellCheckbox: {
          padding: 0,
        },
        scrollbarFiller: {
          "&.MuiDataGrid-scrollbarFiller--pinnedRight": {
            display: "none",
          },
        },
        columnHeader: () => ({
          "&:focus-within": {
            outline: "none",
          },
          "&--withLeftBorder.MuiDataGrid-columnHeader--pinnedRight.MuiDataGrid-columnHeader--last":
            {
              right: "0px !important",
              minWidth:
                "calc(var(--DataGrid-rightPinnedWidth) + var(--DataGrid-hasScrollY) * var(--DataGrid-scrollbarSize)) !important",
            },
        }),
        columnHeaderCheckbox: {
          padding: 0,
        },
        menu: {
          "--DataGrid-rowBorderColor": "var(--modus-wc-color-gray-6)",
          "--unstable_DataGrid-radius": `var(--modus-wc-border-radius-table)`,
          "& .MuiPaper-root": {
            border: "1px solid var(--DataGrid-rowBorderColor)",
            borderRadius: "var(--unstable_DataGrid-radius)",
          },
        },
        row: {
          "&:hover": {
            backgroundColor: "inherit",
          },
          "&--detailPanelExpanded": {
            backgroundColor: "var(--DataGrid-t-color-background-base)",
            "&:hover": {
              backgroundColor: "var(--DataGrid-t-color-background-base)",
            },
          },
        },
        toolbar: {
          borderBottom: 0,
          padding: 0,
        },
        filterForm: {
          // Hide Operator/Value labels (visibility keeps inputs aligned).
          "& .MuiFormControl-root:has(.MuiDataGrid-filterFormOperatorInput), & .MuiDataGrid-filterFormValueInput":
            {
              "& .MuiInputLabel-root": { visibility: "hidden" },
              "& .MuiOutlinedInput-notchedOutline legend": { display: "none" },
            },
          // Drop the caret only on locked operators.
          "& .MuiDataGrid-filterFormOperatorInput .MuiSelect-icon.Mui-disabled":
            {
              display: "none",
            },
          // Hide the read-only And/Or selects on secondary rows.
          "& .MuiFormControl-root:has(.MuiDataGrid-filterFormLogicOperatorInput.Mui-disabled)":
            {
              display: "none",
            },
          // Lift the single active And/Or above its row, aligned under Columns.
          "&:has(.MuiDataGrid-filterFormLogicOperatorInput:not(.Mui-disabled))":
            {
              position: "relative",
              paddingTop: "44px",
              "& .MuiFormControl-root:has(.MuiDataGrid-filterFormLogicOperatorInput)":
                {
                  position: "absolute",
                  top: 0,
                  left: "42px",
                  margin: 0,
                  "& .MuiDataGrid-filterFormLogicOperatorInput": { width: 150 },
                },
            },
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableRipple: true,
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          textTransform: "none",
        },
        sizeSmall: {
          fontWeight: 600,
          fontSize: 14,
        },
        sizeMedium: ({ theme }) => ({
          fontWeight: 700,
          fontSize: 16,
          paddingTop: theme.spacing(1),
          paddingBottom: theme.spacing(1),
        }),
        outlined: {
          borderWidth: 2,
        },
      },
    },
    MuiTabPanel: {
      styleOverrides: {
        root: {
          padding: 0,
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontSize: "16px",
          fontWeight: 600,
          lineHeight: "24px",
          transform: "unset",
          position: "unset",
        },
        asterisk: {
          color: "red",
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        notchedOutline: {
          borderColor: "var(--modus-wc-color-gray-6)",
          top: 0,
          legend: {
            display: "none",
          },
        },
      },
    },
    MuiPickersOutlinedInput: {
      styleOverrides: {
        notchedOutline: {
          borderColor: "var(--modus-wc-color-gray-6)",
          top: 0,
          legend: {
            display: "none",
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          backgroundColor: "var(--modus-wc-color-white)",
        },
        inputSizeSmall: {
          fontSize: "14px",
          paddingTop: "6px !important",
          paddingBottom: "6px !important",
        },
      },
    },
    MuiPickersInputBase: {
      styleOverrides: {
        root: {
          "&.MuiPickersInputBase-inputSizeSmall": {
            fontSize: "14px",
            "& .MuiPickersInputBase-sectionsContainer": {
              paddingTop: "6px !important",
              paddingBottom: "6px !important",
            },
          },
        },
      },
    },
    MuiIcon: {
      defaultProps: {
        baseClassName: "modus-icons",
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          margin: 0,
          fontSize: "0.875rem",
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: ({ theme }) => ({
          fontSize: "1.25rem",
          fontWeight: 500,
          color: theme.palette.text.primary,
        }),
        asterisk: {
          color: "red",
        },
      },
    },
    MuiAccordionSummary: {
      defaultProps: {
        expandIcon: <Icon name="expand_more" />,
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          "&:before": {
            display: "none",
          },
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          backgroundColor: "var(--modus-wc-color-gray-0)",
          color: "var(--modus-wc-color-black)",
          [`&.${toggleButtonGroupClasses.selected}, &.${toggleButtonGroupClasses.selected}:hover`]:
            {
              backgroundColor: "var(--modus-wc-color-black)",
              color: "var(--modus-wc-color-white)",
            },
        },
      },
    },
  },
};

export const AXPAppTheme = createTheme(AXPAppThemeOptions);
